"use client";

import { useEffect, useState } from "react";
import { useCart } from "@/lib/cart-context";

const NAV_LINKS = [
  { href: "#categories", label: "Boutiques" },
  { href: "#promotions", label: "Promotions" },
  { href: "#chef-picks", label: "Sélection" },
  { href: "#experience", label: "Expérience" },
  { href: "#recipes", label: "La Table" },
  { href: "#services", label: "Services" },
  { href: "#location", label: "Adresse" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalCount, openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Top bar - exactement comme l'original */}
      <div className="hidden md:block bg-ink text-cream/70 text-[11px] tracking-[0.2em] uppercase">
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-9">
          <div className="flex items-center gap-6">
            <span>Livraison offerte dès 25 000 F</span>
            <span className="text-cream/30">·</span>
            <span>Click &amp; Collect 30 min</span>
            <span className="text-cream/30">·</span>
            <span>Conciergerie 7j/7 — +33 1 42 31 10 00</span>
          </div>
          <div className="flex items-center gap-6">
            <span>Paris 9ᵉ · Haussmann</span>
            <span className="text-cream/30">·</span>
            <span>FR · EN · WO</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/95 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(26,23,20,0.12)]"
            : "bg-cream/80 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a href="#top" className="group flex items-center gap-3">
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/40">
                <span className="font-display italic text-gold text-2xl leading-none">
                  O
                </span>
                <span className="absolute -top-1 -right-1 text-gold text-xs">
                  ✦
                </span>
              </span>
              <div className="flex flex-col leading-none">
                <span className="font-display text-ink text-xl md:text-2xl tracking-wide">
                  Hyper <span className="gold-text italic">OR</span> I
                </span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-stone mt-1">
                  SENE &amp; DIA
                </span>
              </div>
            </a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="relative text-ink/85 hover:text-gold transition-colors duration-300 text-xs lg:text-sm tracking-[0.18em] uppercase group whitespace-nowrap"
                >
                  {link.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 group-hover:w-full bg-gold transition-all duration-500"></span>
                </a>
              ))}
            </nav>

            {/* Right cluster */}
            <div className="flex items-center gap-2 md:gap-3">
              <button
                aria-label="Rechercher"
                className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full text-ink/70 hover:text-gold hover:bg-parchment/60 transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                </svg>
              </button>
              <button
                aria-label="Compte"
                className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full text-ink/70 hover:text-gold hover:bg-parchment/60 transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
              <button
                aria-label="Panier"
                onClick={openCart}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:text-gold hover:bg-parchment/60 transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                  <path d="M3 6h18"></path>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                {totalCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-gold text-ink text-[10px] font-semibold flex items-center justify-center">
                    {totalCount > 99 ? "99+" : totalCount}
                  </span>
                )}
              </button>
              <button
                aria-label="Menu"
                onClick={() => setMobileOpen((v) => !v)}
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:text-gold hover:bg-parchment/60 transition-all"
              >
                {mobileOpen ? (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 6h16"></path>
                    <path d="M4 12h16"></path>
                    <path d="M4 18h16"></path>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-ink/10 bg-cream/95 backdrop-blur-xl">
            <nav className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-ink/85 hover:text-gold transition-colors text-sm tracking-[0.18em] uppercase"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
